import axios from 'axios';
import { getCookie, setCookie, removeCookie, AUTH_COOKIE_KEYS } from '../utils/cookies';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5254/api';

// Helper to get token from localStorage or cookies
const getAccessToken = () => {
  const token = localStorage.getItem('accessToken');
  if (token && token.length > 0) return token;
  return getCookie(AUTH_COOKIE_KEYS.ACCESS_TOKEN);
};

const getRefreshToken = () => {
  const token = localStorage.getItem('refreshToken');
  if (token && token.length > 0) return token;
  return getCookie(AUTH_COOKIE_KEYS.REFRESH_TOKEN);
};

// Helper to save tokens to both localStorage and cookies
const saveTokens = (accessToken, refreshToken) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  setCookie(AUTH_COOKIE_KEYS.ACCESS_TOKEN, accessToken, 1);
  setCookie(AUTH_COOKIE_KEYS.REFRESH_TOKEN, refreshToken, 365);
};

// Helper to clear all tokens
const clearAllTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  removeCookie(AUTH_COOKIE_KEYS.ACCESS_TOKEN);
  removeCookie(AUTH_COOKIE_KEYS.REFRESH_TOKEN);
  removeCookie(AUTH_COOKIE_KEYS.USER);
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Настроенный экземпляр axios
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Интерцептор для добавления токена авторизации
 */
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Интерцептор для обработки ошибок и автоматического обновления токена
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/');

    // Handle 401 errors (except for auth endpoints and already retried requests)
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      // No refresh token - redirect to login
      if (!refreshToken) {
        isRefreshing = false;
        clearAllTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Request new tokens using refresh endpoint
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { accessToken, refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

        // Save new tokens to localStorage and cookies
        saveTokens(newAccessToken, newRefreshToken);

        // Update default header
        api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Process queued requests
        processQueue(null, newAccessToken);

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        processQueue(refreshError, null);
        clearAllTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
